import { sb } from '../supabase';
import { eventAr, sourceName } from './growthLabels';
import { BLOCK_AR } from './growthProducts';

// Growth OS «سجل الجلسات» — owner, 2026-11-01. Every session in the range,
// read from growth_trader_events (no e2e rows, no team accounts, no supplier devices);
// one session opens as its timeline. City is not collected (owner: it stays
// inside the Clarity recording).

export const STAGES = [
  { key: 'browse', ar: 'تصفّح فقط' },
  { key: 'product', ar: 'شاهد منتجاً' },
  { key: 'cart', ar: 'وصل للسلة' },
  { key: 'checkout', ar: 'بدأ الدفع' },
  { key: 'paid', ar: 'دفع' },
];
export const stageAr = (k) => (STAGES.find((s) => s.key === k) || STAGES[0]).ar;
const DEVICE_AR = { mobile: 'جوال', tablet: 'جهاز لوحي', desktop: 'كمبيوتر' };
export const deviceAr = (d) => DEVICE_AR[d] || (d ? d : '—');

const num = (v) => Number(v) || 0;
const fmtN = (n, d = 0) => Number(n).toLocaleString('en-US', { maximumFractionDigits: d });
const sar = (n) => `${fmtN(n, 2)} ر.س`;

// Where the visit came from: an app open, a tagged link, a referring site, or direct.
export function sourceOf(start, platform) {
  if (platform === 'ios' || platform === 'android') return { key: 'app', ar: 'التطبيق' };
  const p = start?.props || {};
  if (p.utm_source) {
    const paid = /cpc|paid|ads?$/i.test(p.utm_medium || '') || !!(p.gclid || p.fbclid || p.ttclid);
    const name = sourceName(p.utm_source);
    return { key: `utm:${name}`, ar: paid ? `${name} · إعلان` : name };
  }
  if (p.gclid) return { key: 'utm:Google', ar: 'Google · إعلان' };
  if (p.fbclid) return { key: 'utm:Meta', ar: 'Meta · إعلان' };
  if (p.ttclid) return { key: 'utm:TikTok', ar: 'TikTok · إعلان' };
  if (p.referrer_domain) { const n = sourceName(p.referrer_domain); return { key: `ref:${n}`, ar: n }; }
  return { key: 'direct', ar: 'مباشر' };
}

export function stageOf(names, rows) {
  const has = (n) => names.has(n);
  if (has('payment_succeeded') || rows.some((e) => e.event_name === 'payment_returned' && e.props?.result === 'paid')) return 'paid';
  if (has('checkout_started') || has('checkout_step_viewed') || has('payment_started')) return 'checkout';
  if (has('cart_item_added') || rows.some((e) => e.event_name === 'cart_viewed' && num(e.props?.lines) > 0)) return 'cart';
  if (has('product_viewed')) return 'product';
  return 'browse';
}

// Rows (any order) → one summary per session, newest first.
export function summarizeSessions(rows, teamIds = new Set()) {
  const by = {};
  (rows || []).forEach((e) => { if (e.session_id) (by[e.session_id] = by[e.session_id] || []).push(e); });
  return Object.entries(by).map(([id, list]) => {
    list.sort((a, b) => (a.occurred_at < b.occurred_at ? -1 : 1));
    if (list.some((e) => e.user_id && teamIds.has(e.user_id))) return null;
    const names = new Set(list.map((e) => e.event_name));
    const start = list.find((e) => e.event_name === 'session_started');
    const first = list[0]; const last = list[list.length - 1];
    const platform = start?.platform || first.platform;
    const clarity = [...new Set(list.filter((e) => e.event_name === 'clarity_session_linked').map((e) => `${e.props?.clarity_user}/${e.props?.clarity_session}`))];
    return {
      session_id: id,
      at: Date.parse(first.occurred_at),
      end: Date.parse(last.occurred_at),
      seconds: Math.max(0, Math.round((Date.parse(last.occurred_at) - Date.parse(first.occurred_at)) / 1000)),
      source: sourceOf(start, platform),
      device: platform === 'ios' || platform === 'android' ? 'mobile' : (start?.props?.device_class || null),
      os: start?.props?.os || (platform === 'ios' ? 'iOS' : platform === 'android' ? 'Android' : null),
      landing: start?.props?.landing_path || first.screen || null,
      pages: list.filter((e) => e.event_name === 'screen_viewed').length,
      products: new Set(list.filter((e) => e.event_name === 'product_viewed').map((e) => e.props?.product_id)).size,
      stage: stageOf(names, list),
      signedIn: list.some((e) => !!e.user_id),
      events: list.length,
      clarityCount: clarity.length,
    };
  }).filter(Boolean).sort((a, b) => b.at - a.at);
}

export function applyFilters(list, f = {}) {
  return list.filter((s) => (!f.src || s.source.key === f.src)
    && (!f.dev || s.device === f.dev)
    && (!f.stage || s.stage === f.stage)
    && (!f.auth || (f.auth === 'in' ? s.signedIn : !s.signedIn)));
}

export const fmtDuration = (sec) => {
  if (sec < 60) return `${sec} ث`;
  const m = Math.floor(sec / 60); const s = sec % 60;
  if (m < 60) return s ? `${m} د ${s} ث` : `${m} د`;
  return `${Math.floor(m / 60)} س ${m % 60} د`;
};

export const ROW_CAP = 20000;
async function teamOf(userIds) {
  if (!userIds.length) return new Set();
  const { data, error } = await sb.from('profiles').select('id').in('id', userIds).in('role', ['admin', 'super_admin']);
  if (error) throw error;
  return new Set((data || []).map((p) => p.id));
}

export async function fetchSessions(win) {
  const { data, error } = await sb.from('growth_trader_events')
    .select('session_id, event_name, occurred_at, props, user_id, platform, screen')
    .gte('occurred_at', new Date(win.start).toISOString()).lt('occurred_at', new Date(win.end).toISOString())
    .order('occurred_at', { ascending: true }).limit(ROW_CAP);
  if (error) throw error;
  const rows = data || [];
  const team = await teamOf([...new Set(rows.map((r) => r.user_id).filter(Boolean))]);
  return { sessions: summarizeSessions(rows, team), capped: rows.length >= ROW_CAP };
}

// ── One session ──────────────────────────────────────────────────────────────
const STEP_AR = { address: 'العنوان', review: 'مراجعة الطلب', signin: 'تسجيل الدخول', payment_start_error: 'خطأ عند بدء الدفع', order_error: 'خطأ في إنشاء الطلب', 'order_error:quote_expired': 'انتهت صلاحية السعر' };
const SHIP_AR = { air: 'جوي', sea: 'بحري', fast: 'أسرع', express: 'إكسبريس' };
const EXIT_AR = { cart: 'إلى السلة', checkout: 'إلى الدفع', other_product: 'إلى منتج آخر', back_to_list: 'رجع للقائمة', other: 'إلى صفحة أخرى' };
const MODE_AR = { login: 'دخول', signup: 'تسجيل جديد' };
const RESULT_AR = { paid: 'دفع بنجاح', error: 'خطأ', not_paid: 'لم يدفع', success: 'نجح', failed: 'فشل' };
const stepAr = (s) => STEP_AR[s] || (String(s || '').startsWith('quote_error') ? 'خطأ في تسعير الطلب' : s);

// The details we saved for one event, as one line of Arabic. `products` maps id → name.
export function describe(e, products = {}) {
  const p = e.props || {};
  const name = (id) => (id && products[id]) || (id ? 'منتج' : null);
  const parts = [];
  const add = (v) => { if (v != null && v !== '' && v !== false) parts.push(v); };
  switch (e.event_name) {
    case 'session_started': add(sourceOf(e, e.platform).ar); add(p.landing_path && `أول صفحة ${p.landing_path}`); add(deviceAr(p.device_class)); add(p.os); add(p.language && `لغة ${p.language}`); break;
    case 'screen_viewed': add(e.screen); break;
    case 'product_viewed': add(name(p.product_id)); add(p.price_sar != null && sar(p.price_sar)); add(p.moq && `أقل كمية ${fmtN(p.moq)}`); add(p.delivery_label); break;
    case 'product_block_seen': add(BLOCK_AR[p.block] || p.block); add(name(p.product_id)); break;
    case 'product_left': add(name(p.product_id)); add(p.dwell_ms != null && `بقي ${fmtDuration(Math.round(num(p.dwell_ms) / 1000))}`); add(p.max_scroll_pct != null && `مرّر ${fmtN(p.max_scroll_pct)}%`); add(p.last_block && `آخر ما رآه: ${BLOCK_AR[p.last_block] || p.last_block}`); add(p.added ? 'أضاف للسلة' : 'لم يضف'); add(EXIT_AR[p.exit]); break;
    case 'product_qty_changed': add(name(p.product_id)); add(`${fmtN(num(p.qty_before))} ← ${fmtN(num(p.qty_after))}`); add(p.below_moq && 'أقل من الحد الأدنى'); break;
    case 'product_variant_changed': add(name(p.product_id)); add(p.price_after != null && sar(p.price_after)); break;
    case 'product_qty_table_seen': add(name(p.product_id)); add(p.seconds_after_open != null && `بعد ${fmtN(p.seconds_after_open)} ث`); break;
    case 'shipping_option_viewed': add((p.options || []).length > 1 && p.options.map((o) => SHIP_AR[o] || o).join(' / ')); add(p.air_price_sar != null && `جوي ${sar(p.air_price_sar)}`); add(p.sea_price_sar != null && `بحري ${sar(p.sea_price_sar)}`); break;
    case 'shipping_option_selected': add(SHIP_AR[p.option] || p.option); add(p.price_sar != null && sar(p.price_sar)); add(p.delivery_days && `${p.delivery_days} يوماً`); add(name(p.product_id)); break;
    case 'search_submitted': case 'search_refined': add(p.query && `«${p.query}»`); break;
    case 'search_results_shown': add(p.query && `«${p.query}»`); add(p.zero_results ? 'بلا نتائج' : p.result_count != null && `${fmtN(p.result_count)} نتيجة`); break;
    case 'search_result_clicked': add(p.query && `«${p.query}»`); add(name(p.product_id)); add(p.position != null && `الترتيب ${fmtN(num(p.position) + 1)}`); break;
    case 'search_abandoned': add(p.query && `«${p.query}»`); add(p.seconds_on_results != null && `بعد ${fmtN(p.seconds_on_results)} ث`); break;
    case 'cart_item_added': add(name(p.product_id)); add(p.qty != null && `${fmtN(p.qty)} قطعة`); add(p.unit_price_sar != null && `${sar(p.unit_price_sar)} للقطعة`); add(p.line_sar != null && `المجموع ${sar(p.line_sar)}`); add(SHIP_AR[p.shipping_option]); break;
    case 'cart_item_qty_changed': add(name(p.product_id)); add(`${fmtN(num(p.qty_before))} ← ${fmtN(num(p.qty_after))}`); break;
    case 'cart_item_removed': add(name(p.product_id)); add(p.qty != null && `${fmtN(p.qty)} قطعة`); break;
    case 'cart_viewed': add(num(p.lines) === 0 ? 'السلة فارغة' : `${fmtN(p.lines)} منتج`); add(num(p.lines) > 0 && p.total_sar != null && `الإجمالي ${sar(p.total_sar)}`); break;
    case 'cart_minimum_blocked': add(p.total_sar != null && `الإجمالي ${sar(p.total_sar)}`); add(p.gap_sar != null && `ينقصه ${sar(p.gap_sar)}`); break;
    case 'cart_shipping_changed': add(`${SHIP_AR[p.from_option] || p.from_option || '—'} ← ${SHIP_AR[p.to_option] || p.to_option || '—'}`); break;
    case 'checkout_started': add(p.total_sar != null && `الإجمالي ${sar(p.total_sar)}`); add(p.lines != null && `${fmtN(p.lines)} منتج`); add(SHIP_AR[p.shipping_option]); add(p.signed_in === false && 'غير مسجّل'); break;
    case 'checkout_step_viewed': add(stepAr(p.step)); break;
    case 'address_submitted': add(p.valid === false ? `خطأ في ${p.field_with_error || 'حقل'}` : 'صحيح'); break;
    case 'payment_started': add(p.amount_sar != null && sar(p.amount_sar)); break;
    case 'payment_returned': add(RESULT_AR[p.result] || p.result); add(p.seconds_at_gateway != null && `بعد ${fmtDuration(num(p.seconds_at_gateway))} في صفحة الدفع`); break;
    case 'auth_viewed': add(MODE_AR[p.mode] || p.mode); add(p.trigger === 'checkout' ? 'من الدفع' : p.trigger === 'direct' ? 'مباشرة' : p.trigger); break;
    case 'auth_method_chosen': case 'login_completed': case 'signup_completed': add(p.method === 'phone' ? 'بالجوال' : p.method === 'email' ? 'بالبريد' : p.method === 'google' ? 'بـ Google' : p.method); break;
    case 'otp_requested': case 'otp_verified': case 'otp_failed': add(p.channel === 'whatsapp' ? 'واتساب' : p.channel === 'sms' ? 'رسالة نصية' : p.channel === 'email' ? 'بريد' : p.channel); add(p.reason); break;
    case 'page_hidden': add(p.seconds_on_screen != null && `بعد ${fmtDuration(num(p.seconds_on_screen))} على الصفحة`); add(e.screen); break;
    case 'category_viewed': add(p.subcategory || p.category); add(p.product_count != null && `${fmtN(p.product_count)} منتج`); break;
    case 'clarity_session_linked': add('تسجيل جديد في Clarity لهذه الصفحة'); break;
    default:
      Object.entries(p).slice(0, 4).forEach(([k, v]) => add(`${k}: ${typeof v === 'object' ? JSON.stringify(v) : v}`));
  }
  return parts.filter((x) => typeof x === 'string' || typeof x === 'number').join(' · ');
}

// Where the session ended: the last thing that was not leaving the page.
export function stoppedAt(list) {
  const quiet = new Set(['page_hidden', 'app_backgrounded', 'clarity_session_linked', 'product_left', 'search_abandoned']);
  const last = [...list].reverse().find((e) => !quiet.has(e.event_name));
  return last ? { label: eventAr(last.event_name), at: Date.parse(last.occurred_at), screen: last.screen } : null;
}

export async function fetchSessionTimeline(sessionId) {
  const { data, error } = await sb.from('growth_trader_events')
    .select('event_id, session_id, event_name, occurred_at, props, user_id, platform, screen')
    .eq('session_id', sessionId).order('occurred_at', { ascending: true }).limit(2000);
  if (error) throw error;
  const list = data || [];
  const team = await teamOf([...new Set(list.map((r) => r.user_id).filter(Boolean))]);
  if (list.some((e) => e.user_id && team.has(e.user_id))) return { team: true, list: [] };
  const ids = [...new Set(list.map((e) => e.props?.product_id).filter(Boolean))];
  let products = {};
  if (ids.length) {
    const { data: ps } = await sb.from('market_products_public').select('id, title_ar, title_en').in('id', ids);
    products = Object.fromEntries((ps || []).map((x) => [x.id, x.title_ar || x.title_en]));
  }
  return { team: false, list, products, summary: summarizeSessions(list)[0] || null };
}

export const sessionLogPath = (sessionId, search = '') => {
  const p = new URLSearchParams(search);
  p.set('s', sessionId);
  return `/admin2/growth/sessions?${p.toString()}`;
};

// A given set of sessions (from a «شاهد الجلسات» / «توقفوا هنا» list), whatever the range.
export async function fetchSessionsByIds(ids) {
  const list = [...new Set(ids)].filter((x) => /^[0-9a-f-]{36}$/i.test(x)).slice(0, 50);
  if (!list.length) return { sessions: [], capped: false };
  const { data, error } = await sb.from('growth_trader_events')
    .select('session_id, event_name, occurred_at, props, user_id, platform, screen')
    .in('session_id', list).order('occurred_at', { ascending: true }).limit(ROW_CAP);
  if (error) throw error;
  const rows = data || [];
  const team = await teamOf([...new Set(rows.map((r) => r.user_id).filter(Boolean))]);
  return { sessions: summarizeSessions(rows, team), capped: false };
}
export const sessionsListPath = (ids) => `/admin2/growth/sessions?ids=${ids.slice(0, 50).join(',')}`;

// Leaving and coming back to the same page (switching tabs) is recorded as one
// «غادر الصفحة» each time; in the timeline a run of them on one page is ONE
// line (owner, 2026-11-01): «غادر الصفحة ورجع لها 3 مرات · آخر مرة بعد 5 د 38 ث».
// A Clarity link in between does not break the run (it is not a visitor action).
const PASSIVE = new Set(['clarity_session_linked']);
const timesAr = (n) => (n === 1 ? 'مرة واحدة' : n === 2 ? 'مرتين' : n <= 10 ? `${n} مرات` : `${n} مرة`);
export function groupTimeline(list) {
  const out = [];
  let run = null;
  (list || []).forEach((e) => {
    if (e.event_name === 'page_hidden') {
      if (run && run.e.screen === e.screen) { run.count += 1; run.last = e; return; }
      run = { e, count: 1, last: e };
      out.push(run);
      return;
    }
    if (!PASSIVE.has(e.event_name)) run = null;
    out.push({ e, count: 1, last: e });
  });
  return out;
}
export function hiddenRunText(item) {
  if (item.count < 2) return null;
  const secs = num(item.last.props?.seconds_on_screen);
  return `غادر الصفحة ورجع لها ${timesAr(item.count - 1)}${secs ? ` · آخر مرة بعد ${fmtDuration(secs)}` : ''}`;
}
